import { ErrorMessage, Field, Form, Formik } from "formik";
import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Col, Row, TabPane, Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import Swal from "sweetalert2";
import * as Yup from "yup";
import { DASHBOARD_USER_ROLES } from "../../../../constants";
import { useCurrentUser } from "../../../../slices/auth/hooks";
import { paymentMethod } from "../../../../slices/payment/thunk";
import BackButton from "../../components/BackButton";
import NextButton from "../../components/NextButton";
// Add these imports for Terms of Service and Privacy Policy PDFs
import terms_of_service from "../../../../assets/pdf/agreements/producer-agreement.pdf";
import privacy_policy from "../../../../assets/pdf/agreements/producer-agreement.pdf";

const PaymentNewStep = ({
  toggleVerticalTab,
  activeVerticalTab,
  onFinishOnboarding,
  paymentStep,
}) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const currentUser = useCurrentUser();
  const step = searchParams.get("step");
  const status = searchParams.get("status");

  const [agreement, setAgreement] = React.useState(false);
  const [baxRxAgreement, setBaxRxAgreement] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  // Modal states for Terms of Service and Privacy Policy
  const [termsModal, setTermsModal] = React.useState(false);
  const [privacyModal, setPrivacyModal] = React.useState(false);

  const isBroker = currentUser.role === DASHBOARD_USER_ROLES.BROKER;
  const isGA = currentUser.role === DASHBOARD_USER_ROLES.GA;
  const isEmployer = currentUser.role === DASHBOARD_USER_ROLES.EMPLOYER;

  const disableSumbit = loading || !agreement || !baxRxAgreement;

  const toggleTermsModal = () => setTermsModal(!termsModal);
  const togglePrivacyModal = () => setPrivacyModal(!privacyModal);

  const handleDownload = (file, customFileName) => {
    const link = document.createElement("a");
    link.href = file;
    link.setAttribute("download", customFileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (!step || !status) {
      return;
    }
    if (step === "payment" && status === "return") {
      onFinishOnboarding();
    } else if (
      (step === "payment" && status === "refresh") ||
      (step === "payment" && status === "error")
    ) {
      Swal.fire({
        icon: "error",
        title: "Oops!",
        text: "There was a problem completing your bank setup. Please try again.",
      });
      toggleVerticalTab(8);
    }
  }, [step, status]);

  const initialValues = {
    account_holder_first: "",
    account_holder_last: "",
    account_number: "",
    routing_number: "",
  };

  const validationSchema = Yup.object().shape({
    account_holder_first: Yup.string().required("First Name is required"),
    account_holder_last: Yup.string().required("Last Name is required"),
    account_number: Yup.string()
      .min(6, "Bank Account Number must be at least 6 characters")
      .required("Bank Account Number is required"),
    routing_number: Yup.string()
      .test(
        "is-9-digits",
        "Routing Number must be 9 digits long without spaces",
        (value) => {
          console.log(value);
          return /^[0-9]+$/.test(value);
        }
      )
      .required("Routing Number is required"),
  });

  const handleSubmit = async (values) => {
    if (disableSumbit) {
      return;
    }

    setLoading(true);
    try {
      Swal.fire({
        title: "Please wait...",
        html: "Updating information...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const actionResult = await dispatch(
        paymentMethod({
          account_holder: `${values.account_holder_first} ${values.account_holder_last}`,
          account_number: values.account_number,
          routing_number: values.routing_number,
        })
      );

      const { payload, error } = actionResult;

      if (error) {
        Swal.fire({
          icon: "error",
          title: "Payment Error",
          text:
            payload.message || "There was an error processing your request.",
        });
      } else {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Your payment method has been added successfully.",
        });
        toggleVerticalTab(activeVerticalTab + (isBroker || isGA ? 2 : 1));
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Submission Error",
        text: "There was an issue submitting your payment method. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Get the appropriate BaxRx agreement text based on user role
  const getBaxRxAgreementText = () => {
    if (isEmployer) {
      return (
        <>
          I acknowledge and agree that this platform is operated by BaxRx Inc., which manages subscription services, whether branded directly or through a partner. I understand that billing may be handled by BaxRx or an authorized third party, and that if BaxRx handles billing, it will appear as the billing entity on any invoice or payment statement. I agree to the{" "}
          <a
            className="p-0 text-decoration-underline cursor-pointer"
            onClick={toggleTermsModal}
            style={{ verticalAlign: "baseline" }}
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            className="p-0 text-decoration-underline cursor-pointer"
            onClick={togglePrivacyModal}
            style={{ verticalAlign: "baseline" }}
          >
            Privacy Policy
          </a>{" "}
          of BaxRx Inc.
        </>
      );
    } else {
      // For brokers and GAs
      return (
        <>
          I acknowledge and agree that this platform is operated by BaxRx Inc., which manages subscription services and service delivery. I understand that commissions may be paid directly by BaxRx or through my agency or supervising entity, and that if paid by BaxRx, the payment will appear from BaxRx Inc. on my commission statement. I agree to the{" "}
          <a
            className="p-0 text-decoration-underline cursor-pointer"
            onClick={toggleTermsModal}
            style={{ verticalAlign: "baseline" }}
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            className="p-0 text-decoration-underline cursor-pointer"
            onClick={togglePrivacyModal}
            style={{ verticalAlign: "baseline" }}
          >
            Privacy Policy
          </a>{" "}
          of BaxRx Inc.
        </>
      );
    }
  };

  return (
    <TabPane tabId={paymentStep}>
      <div>
        <h5>Payment Information</h5>
        <p className="text-muted">
          {isEmployer
            ? "Awesome, now into payment information. This bank account will be used to make ACH transfers to pay for memberships for your employees"
            : "Almost done! This bank account will be used to send you commissions via ACH directly on this portal."}
        </p>
        <p className="text-muted">
          Please complete the form below to set up your bank account.
        </p>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid, dirty }) => (
          <Form className="border rounded-4 p-3 ">
            <Row>
              <Col className="col-6">
                <div className="mb-3">
                  <label className="form-label">
                    Account Holder First Name
                    <span className="text-danger">*</span>
                  </label>
                  <Field
                    type="text"
                    className="form-control"
                    placeholder="Enter your account holder First Name"
                    name="account_holder_first"
                  />
                  <ErrorMessage
                    name="account_holder_first"
                    component="div"
                    className="text-danger"
                  />
                </div>
              </Col>
              <Col className="col-6">
                <div className="mb-3">
                  <label className="form-label">
                    Account Holder Last Name
                    <span className="text-danger">*</span>
                  </label>
                  <Field
                    type="text"
                    className="form-control"
                    placeholder="Enter your account holder last name "
                    name="account_holder_last"
                  />
                  <ErrorMessage
                    name="account_holder_last"
                    component="div"
                    className="text-danger"
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col className="col-6">
                <div className="mb-3">
                  <label className="form-label">
                    Bank Account Number
                    <span className="text-danger">*</span>
                  </label>
                  <Field
                    type="number"
                    className="form-control"
                    placeholder="Enter your bank account number"
                    name="account_number"
                  />
                  <ErrorMessage
                    name="account_number"
                    component="div"
                    className="text-danger"
                  />
                </div>
              </Col>
              <Col className="col-6">
                <div className="mb-3">
                  <label className="form-label">
                    Routing Number
                    <span className="text-danger">*</span>
                  </label>
                  <Field
                    className="form-control"
                    placeholder="Enter your routing number"
                    name="routing_number"
                    onKeyDown={(e) => {
                      if (e.key === " " || e.key === "Spacebar") {
                        e.preventDefault();
                      }
                    }}
                    onInput={(e) => {
                      e.target.value = e.target.value.slice(0, 9);
                    }}
                  />
                  <ErrorMessage
                    name="routing_number"
                    component="div"
                    className="text-danger"
                  />
                </div>
              </Col>
            </Row>

            <Row className="mt-2">
              <Col className="col-12">
                <div className="mb-3">
                  <label className="form-label text-muted">
                    <Field
                      checked={agreement}
                      onChange={() => setAgreement(!agreement)}
                      type="checkbox"
                      className="form-check-input me-2"
                      name="authorize"
                    />
                    I authorize and digitally sign for BaxRx to electronically debit or credit the bank account provided for recurring monthly payments or transactions, including any applicable fees. I understand that this authorization will remain in effect until I provide notice of cancellation. Details outlined in the{" "}
                    <a
                      className="p-0 text-decoration-underline cursor-pointer"
                      onClick={toggleTermsModal}
                      style={{ verticalAlign: "baseline" }}
                    >
                      Terms of Service
                    </a>.
                  </label>
                  <ErrorMessage
                    name="authorize"
                    component="div"
                    className="text-danger"
                  />
                </div>
              </Col>
            </Row>

            {/* New BaxRx acknowledgment checkbox */}
            <Row className="mt-2">
              <Col className="col-12">
                <div className="mb-3">
                  <label className="form-label text-muted">
                    <Field
                      checked={baxRxAgreement}
                      onChange={() => setBaxRxAgreement(!baxRxAgreement)}
                      type="checkbox"
                      className="form-check-input me-2"
                      name="baxrx_agreement"
                    />
                    {getBaxRxAgreementText()}
                  </label>
                </div>
              </Col>
            </Row>

            <div className="d-flex align-items-start gap-3 mt-4">
              <BackButton
                type={"button"}
                back={() => toggleVerticalTab(activeVerticalTab - 1)}
              />
              <NextButton
                type={"submit"}
                disabled={disableSumbit || !dirty || !isValid}
              />
            </div>
          </Form>
        )}
      </Formik>

      {/* Terms of Service Modal */}
      <Modal isOpen={termsModal} toggle={toggleTermsModal} size="lg">
        <ModalHeader toggle={toggleTermsModal}>Terms of Service</ModalHeader>
        <ModalBody>
          <iframe
            src={terms_of_service}
            style={{ width: "100%", height: "500px" }}
            title="Terms of Service"
          ></iframe>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => handleDownload(terms_of_service, "BaxRx_Terms_of_Service.pdf")}
          >
            Download
          </Button>
          <Button color="primary" onClick={toggleTermsModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Privacy Policy Modal */}
      <Modal isOpen={privacyModal} toggle={togglePrivacyModal} size="lg">
        <ModalHeader toggle={togglePrivacyModal}>Privacy Policy</ModalHeader>
        <ModalBody>
          <iframe
            src={privacy_policy}
            style={{ width: "100%", height: "500px" }}
            title="Privacy Policy"
          ></iframe>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => handleDownload(privacy_policy, "BaxRx_Privacy_Policy.pdf")}
          >
            Download
          </Button>
          <Button color="primary" onClick={togglePrivacyModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </TabPane>
  );
};

export default PaymentNewStep;